import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { Theme } from '@radix-ui/themes';
import { Toaster } from 'react-hot-toast';
import { gsap } from 'gsap';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/auth/ProtectedRoutes';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Sidebar from './components/layout/Sidebar';
import DashboardTopBar from './components/layout/DashboardTopBar';
import SupportWidget from './components/common/SupportWidget';
import Logo from './components/common/Logo';
import LogoutModal from './components/common/LogoutModal';
import LogoMarquee from './components/home/LogoMarquee';
import logoImage from './assets/logo-transparent.png';
import {
  ArrowLeftOnRectangleIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  HeartIcon,
  HomeIcon,
  BellIcon,
  ChartBarIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ScissorsIcon,
  Squares2X2Icon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Public Pages
import Home from './pages/Home';
import Search from './pages/Search';
import ProviderDetails from './pages/ProviderDetails';
import Login from './pages/auth/AuthPage';
import Register from './pages/auth/Register';
import RolePicker from './pages/auth/RolePicker';
import ProviderPlans from './pages/auth/ProviderPlans';
import GoogleOnboarding from './pages/auth/GoogleOnboarding';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BookingPolicy from './pages/BookingPolicy';
import CookiePolicy from './pages/CookiePolicy';
import CookieConsent from './components/common/CookieConsent';
import TermsOfService from './pages/TermsOfService';
import FAQ from './pages/FAQ';

// Client Pages
import Bookings from './pages/Bookings';
import Dashboard from './pages/Dashboard';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Help from './pages/Help';
import AboutUs from './pages/AboutUs';
import PaymentMethods from './pages/PaymentMethods';
import Transactions from './pages/Transactions';
import Notifications from './pages/Notifications';

import MyReviews from './pages/MyReviews';

// Provider Pages
import ProviderDashboard from './pages/provider/ProviderDashboard';
import ProviderBookings from './pages/provider/ProviderBookings';
import ManageServices from './pages/provider/ManageServices';
import ManageAvailability from './pages/provider/ManageAvailabilty';
import BusinessProfile from './pages/provider/BusinessProfile';
import Portfolio from './pages/provider/Portfolio';
import ProviderSettings from './pages/provider/ProviderSettings';
import ProviderAnalytics from './pages/provider/ProviderAnalytics';
import ProviderReviews from './pages/provider/ProviderReviews';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageProviders from './pages/admin/ManageProviders';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import ManageCategories from './pages/admin/ManageCategories';

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AppWithTheme />
      </ThemeProvider>
    </Router>
  );
}

function AppWithTheme() {
  const { isDark } = useTheme();

  return (
    <Theme
      accentColor="orange"
      grayColor="gray"
      appearance={isDark ? 'dark' : 'light'}
      radius="medium"
      scaling="100%"
    >
      <AuthProvider>
        <AppContent />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: isDark ? '#232323' : '#fff',
              color: isDark ? '#eee' : '#1f1f1f',
              border: `1px solid ${isDark ? '#3a3a3a' : '#e8e8e8'}`,
              borderRadius: '10px',
              fontSize: '14px',
            },
            success: {
              duration: 3000,
              iconTheme: { primary: '#22c55e', secondary: '#fff' },
            },
            error: {
              duration: 4000,
              iconTheme: { primary: '#ef4444', secondary: '#fff' },
            },
          }}
        />
      </AuthProvider>
    </Theme>
  );
}

function AppContent() {
  const { showIdleWarning, resetIdleTimer } = useAuth();
  const [showMobileSplash, setShowMobileSplash] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowMobileSplash(false), 2400);
    return () => window.clearTimeout(timer);
  }, []);

  const MobileAppNav = () => {
    const { isAuthenticated, isProvider, isAdmin, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    if (!isAuthenticated) return null;

    const primaryItems = isAdmin
      ? [
          { to: '/admin/dashboard', label: 'Home', icon: HomeIcon },
          { to: '/admin/providers', label: 'Providers', icon: Squares2X2Icon },
          { to: '/admin/categories', label: 'Categories', icon: ScissorsIcon },
        ]
      : isProvider
        ? [
            { to: '/provider/dashboard', label: 'Home', icon: HomeIcon },
            { to: '/provider/bookings', label: 'Bookings', icon: CalendarDaysIcon },
            { to: '/provider/services', label: 'Services', icon: ScissorsIcon },
          ]
        : [
            { to: '/dashboard', label: 'Home', icon: HomeIcon },
            { to: '/bookings', label: 'Bookings', icon: CalendarDaysIcon },
            { to: '/favorites', label: 'Saved', icon: HeartIcon },
          ];

    const moreItems = isAdmin
      ? [
          { to: '/admin/analytics', label: 'Analytics', icon: ChartBarIcon },
          { to: '/admin/dashboard', label: 'Account', icon: UserCircleIcon },
        ]
      : isProvider
        ? [
            { to: '/provider/profile', label: 'Profile', icon: UserCircleIcon },
            { to: '/provider/analytics', label: 'Analytics', icon: ChartBarIcon },
            { to: '/provider/settings', label: 'Settings', icon: Cog6ToothIcon },
          ]
        : [
            { to: '/payment-methods', label: 'Payment Methods', icon: CreditCardIcon },
            { to: '/transactions', label: 'Transactions', icon: DocumentTextIcon },
            { to: '/notifications', label: 'Notifications', icon: BellIcon },
            { to: '/profile', label: 'Profile', icon: UserCircleIcon },
            { to: '/settings', label: 'Settings', icon: Cog6ToothIcon },
          ];

    const moreActive = moreItems.some(({ to }) => location.pathname === to || location.pathname.startsWith(`${to}/`));

    const handleLogout = () => setShowLogoutModal(true);
    const confirmLogout = () => {
      logout();
      setShowMoreMenu(false);
      setShowLogoutModal(false);
      navigate('/login');
    };

    return (
      <>
        <LogoutModal
          isOpen={showLogoutModal}
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
        {showMoreMenu && (
          <div className="lg:hidden fixed inset-0 z-50">
            <button
              type="button"
              aria-label="Close mobile menu"
              className="absolute inset-0"
              style={{ backgroundColor: 'rgba(15, 23, 42, 0.32)' }}
              onClick={() => setShowMoreMenu(false)}
            />
            <div
              className="absolute inset-x-0 bottom-0 rounded-t-3xl border-t px-4 pb-6 pt-4"
              style={{
                backgroundColor: '#fff',
                borderColor: 'var(--gray-4)',
                paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
                boxShadow: '0 -18px 40px rgba(15, 23, 42, 0.18)',
              }}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="min-w-0">
                  <Logo size="small" dark className="mb-1" onClick={() => setShowMoreMenu(false)} />
                  <p className="text-xs" style={{ color: 'var(--gray-9)' }}>Quick access to the rest of your account</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMoreMenu(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: 'var(--gray-3)', color: 'var(--gray-11)' }}
                >
                  <XMarkIcon style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {moreItems.map(({ to, label, icon: Icon }) => {
                  const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 rounded-2xl px-4 py-3"
                      style={{
                        textDecoration: 'none',
                        backgroundColor: active ? 'var(--orange-2)' : 'var(--gray-2)',
                        color: active ? 'var(--orange-10)' : 'var(--gray-11)',
                        border: `1px solid ${active ? 'var(--orange-5)' : 'var(--gray-4)'}`,
                      }}
                    >
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-xl"
                        style={{ backgroundColor: active ? 'rgba(240, 90, 53, 0.14)' : '#fff' }}
                      >
                        <Icon style={{ width: '19px', height: '19px' }} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{label}</p>
                      </div>
                      <ChevronRightIcon style={{ width: '16px', height: '16px', marginLeft: 'auto', flexShrink: 0 }} />
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left"
                  style={{
                    backgroundColor: 'var(--red-2, #fff5f5)',
                    color: 'var(--red-10, #d93d42)',
                    border: '1px solid var(--red-5, #ffd1d9)',
                  }}
                >
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-xl"
                    style={{ backgroundColor: '#fff' }}
                  >
                    <ArrowLeftOnRectangleIcon style={{ width: '19px', height: '19px' }} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">Logout</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        <nav
          className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur"
          style={{
            borderColor: 'var(--gray-4)',
            paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
            boxShadow: '0 -8px 28px rgba(0,0,0,0.08)',
          }}
        >
          <div className="grid grid-cols-4 gap-1 px-2 pt-2">
            {primaryItems.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
              return (
                <Link
                  key={`${to}-${label}`}
                  to={to}
                  className="flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-center"
                  style={{
                    color: active ? 'var(--orange-9)' : 'var(--gray-10)',
                    backgroundColor: active ? 'var(--orange-2)' : 'transparent',
                    textDecoration: 'none',
                  }}
                >
                  <Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                  <span className="text-[10px] font-semibold leading-none truncate max-w-full">
                    {label}
                  </span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setShowMoreMenu(true)}
              className="flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-center"
              style={{
                color: moreActive || showMoreMenu ? 'var(--orange-9)' : 'var(--gray-10)',
                backgroundColor: moreActive || showMoreMenu ? 'var(--orange-2)' : 'transparent',
                border: 'none',
              }}
            >
              <Squares2X2Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <span className="text-[10px] font-semibold leading-none truncate max-w-full">
                More
              </span>
            </button>
          </div>
        </nav>
      </>
    );
  };

  const LayoutWithAuth = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();

    const showSidebar = isAuthenticated;
    const isAuthPage =
      location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname === '/register/client' ||
      location.pathname === '/register/provider' ||
      location.pathname === '/register/provider/plans' ||
      location.pathname === '/auth/onboarding';

    const handleFilterChange = (filters) => {
      sessionStorage.setItem('sidebarFilters', JSON.stringify(filters));
      window.dispatchEvent(new CustomEvent('sidebarFiltersChange', { detail: filters }));
    };

    return (
      <div className="min-h-screen flex flex-col overflow-x-hidden" style={{ backgroundColor: 'var(--color-background)', color: 'var(--gray-12)' }}>
        {!isAuthPage && !isAuthenticated && <Navbar />}
        {!isAuthPage && isAuthenticated && <DashboardTopBar />}
        <div className="flex-1 flex min-w-0" style={{ paddingTop: isAuthPage || isAuthenticated ? '0' : 'var(--public-nav-offset)' }}>
          {showSidebar && (
            <div className="hidden lg:block flex-shrink-0">
              <Sidebar onFilterChange={handleFilterChange} />
            </div>
          )}
          <main className="flex-1 min-w-0 pb-24 lg:pb-0">
            {children}
          </main>
        </div>
        {!isAuthPage && <MobileAppNav />}
        {!isAuthPage && !isAuthenticated && <LogoMarquee />}
        {!isAuthPage && !isAuthenticated && <div className="h-px w-full" style={{ backgroundColor: 'var(--orange-6)' }}></div>}
        {!isAuthPage && !isAuthenticated && <Footer />}
        {!isAuthPage && <SupportWidget mobileRaised={isAuthenticated} />}
      </div>
    );
  };

  const LandingPage = () => {
    const { isAuthenticated, isProvider, isAdmin } = useAuth();
    const location = useLocation();

    if (isAuthenticated && !location.state?.stayOnLanding) {
      if (isProvider) return <Navigate to="/provider/dashboard" replace />;
      if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
      return <Navigate to="/dashboard" replace />;
    }

    return <Home />;
  };

  return (
    <>
      {showMobileSplash && <MobileSplashScreen />}
      <CookieConsent />
      {/* Idle timeout warning */}
      {showIdleWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          backgroundColor: 'var(--red-9, #e5484d)', color: '#fff',
          padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
          fontSize: '14px', fontWeight: 500,
        }}>
          <span>You'll be logged out in 2 minutes due to inactivity</span>
          <button
            onClick={resetIdleTimer}
            style={{
              padding: '4px 16px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.5)',
              backgroundColor: 'transparent', color: '#fff', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stay Logged In
          </button>
        </div>
      )}
    <LayoutWithAuth>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/search" element={<Search />} />
        <Route path="/providers/:id" element={<ProviderDetails />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RolePicker />} />
        <Route path="/register/client" element={<Register />} />
        <Route path="/register/provider/plans" element={<ProviderPlans />} />
        <Route path="/register/provider" element={<Register />} />
        <Route path="/auth/onboarding" element={<GoogleOnboarding />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/policies" element={<BookingPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/faq" element={<FAQ />} />

        {/* Protected Routes */}
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
        <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
        <Route path="/my-reviews" element={<ProtectedRoute><MyReviews /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethods /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

        {/* Provider Routes */}
        <Route path="/provider/dashboard" element={<ProtectedRoute allowedRoles={['provider']}><ProviderDashboard /></ProtectedRoute>} />
        <Route path="/provider/bookings" element={<ProtectedRoute allowedRoles={['provider']}><ProviderBookings /></ProtectedRoute>} />
        <Route path="/provider/services" element={<ProtectedRoute allowedRoles={['provider']}><ManageServices /></ProtectedRoute>} />
        <Route path="/provider/availability" element={<ProtectedRoute allowedRoles={['provider']}><ManageAvailability /></ProtectedRoute>} />
        <Route path="/provider/profile" element={<ProtectedRoute allowedRoles={['provider']}><BusinessProfile /></ProtectedRoute>} />
        <Route path="/provider/portfolio" element={<ProtectedRoute allowedRoles={['provider']}><Portfolio /></ProtectedRoute>} />
        <Route path="/provider/settings" element={<ProtectedRoute allowedRoles={['provider']}><ProviderSettings /></ProtectedRoute>} />
        <Route path="/provider/analytics" element={<ProtectedRoute allowedRoles={['provider']}><ProviderAnalytics /></ProtectedRoute>} />
        <Route path="/provider/reviews" element={<ProtectedRoute allowedRoles={['provider']}><ProviderReviews /></ProtectedRoute>} />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/providers" element={<ProtectedRoute allowedRoles={['admin']}><ManageProviders /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['admin']}><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/categories" element={<ProtectedRoute allowedRoles={['admin']}><ManageCategories /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LayoutWithAuth>
    </>
  );
}

const splashWords = ['Beauty', 'Grooming', 'Wellness'];

const MobileSplashScreen = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const words = gsap.utils.toArray('.mobile-splash__word');

      gsap.set(words, { autoAlpha: 0, y: 16, scale: 0.96 });

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from('.mobile-splash__logo', {
        autoAlpha: 0,
        y: 18,
        scale: 0.9,
        duration: 0.58,
      })
        .from('.mobile-splash__tagline', {
          autoAlpha: 0,
          y: 10,
          duration: 0.38,
        }, '-=0.2')
        .to(words, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.36,
          stagger: 0.16,
        }, '-=0.08')
        .to(words, {
          y: -8,
          autoAlpha: 0,
          duration: 0.28,
          stagger: 0.08,
          ease: 'power2.in',
        }, '+=0.5')
        .to('.mobile-splash__logo, .mobile-splash__tagline', {
          y: -8,
          autoAlpha: 0,
          duration: 0.3,
          ease: 'power2.in',
        }, '-=0.08');
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={rootRef} className="mobile-splash" role="status" aria-label="Loading Salon">
      <div className="mobile-splash__content">
        <img src={logoImage} alt="Salon" className="mobile-splash__logo" />
        <p className="mobile-splash__tagline">Your beauty, booked beautifully</p>
        <div className="mobile-splash__words" aria-hidden="true">
          {splashWords.map((word) => (
            <span key={word} className="mobile-splash__word">
              {word}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
    <div className="text-center px-4">
      <h1 className="text-8xl font-black mb-4" style={{ color: 'var(--orange-9)' }}>404</h1>
      <p className="text-xl mb-8" style={{ color: 'var(--gray-11)' }}>Page not found</p>
      <a
        href="/"
        className="inline-flex items-center px-6 py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: 'var(--orange-9)' }}
      >
        Go Home
      </a>
    </div>
  </div>
);

export default App;
